'use client';

import type React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProjectSitesList, type Filters, type PendingWorkCount } from './project_site';
import api from '../../libs/axios';
import { useAuth } from '../../provider/auth.provider';
import { useDebounce } from '../../hooks/useDebounce';
import toast from 'react-hot-toast';

interface Client {
  id: number;
  client_name: string;
  status: string;
}

interface Activity {
  id: number;
  activity_name: string;
  status: string;
}

interface Project {
  id: number;
  project_name: string;
  status: string;
  client?: Client;
  activity?: Activity;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role_id: number;
}

interface Site {
  id: number;
  site_id: string;
  project_id: number;
  status: string;
  created_at: string;
  updated_at: string;
  created_by: number;
  assigned_engineer_id?: number;
  assigned_ta_id?: number;
  assigned_report_maker_id?: number;
  assigned_validator_id?: number;
  visit_remarks?: string;
  cancel_reason?: string;
  hold_reason?: string;
  project?: Project;
  activity?: Activity;
  site_engineer?: User;
  ta?: User;
  mis?: User;
  report_designer?: User;
  validator?: User;
  survey_workflow_status: string;
  has_pending_billing?: boolean;
}

interface SiteSortConfig {
  field: string;
  direction: 'ASC' | 'DESC';
}

interface Pagination {
  offset: number;
  limit: number;
  page: number;
  total_pages: number;
  total_count: number;
  has_next_page: boolean;
  has_prev_page: boolean;
  next_page: number | null;
  prev_page: number | null;
}

const MySitesPage: React.FC = () => {
  const { user } = useAuth();

  const [sites, setSites] = useState<Site[]>([]);
  const [pendingWorkCounts, setPendingWorkCounts] = useState<PendingWorkCount[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [users, setUsers] = useState<{ [roleId: number]: User[] }>({});
  const [pagination, setPagination] = useState<Pagination>({
    offset: 0,
    limit: 10,
    page: 1,
    total_pages: 1,
    total_count: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null,
  });
  const [sortConfig, setSortConfig] = useState<SiteSortConfig>({
    field: 'created_at',
    direction: 'DESC',
  });
  const [sitesLoading, setSitesLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [activityFilter, setActivityFilter] = useState<string>('');
  const [advancedFilters, setAdvancedFilters] = useState<Filters>({});
  const [exportFilters, setExportFilters] = useState<Filters>({});

  const navigate = useNavigate();

  const fetchSites = async (
    search = '',
    status = '',
    project_id = '',
    activity_id = '',
    page = 1,
    limitValue?: number,
    filters: Filters = advancedFilters,
    sort: SiteSortConfig = sortConfig,
  ) => {
    setSitesLoading(true);
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10;
      const queryParams: any = {
        page,
        limit: effectiveLimit,
        ...filters,
      };

      if (search.trim()) queryParams.search = search;
      if (status.trim()) queryParams.status = status;
      if (project_id.trim()) queryParams.project_id = project_id;
      if (activity_id.trim()) queryParams.activity_id = activity_id;
      if (sort?.field) {
        queryParams.sort_by = sort.field;
        queryParams.sort_order = sort.direction;
      }

      Object.keys(queryParams).forEach((key) => {
        if (queryParams[key] === undefined || queryParams[key] === null) {
          delete queryParams[key];
        } else {
          queryParams[key] = queryParams[key].toString();
        }
      });

      // Uses the "my sites" endpoint — backend filters by current user's assignments for non-admin roles
      const response = await api.get('/survey/sites', {
        params: queryParams,
      });

      if (response.data.success) {
        setSites(response.data.data.sites || []);
        setPagination(response.data.data.pagination || pagination);
        setPendingWorkCounts(response.data.data.pending_work_counts || []);
      }
    } catch (error) {
      console.error('Error fetching my sites:', error);
    } finally {
      setSitesLoading(false);
    }
  };

  // The only filter on My Sites is Project, and it offers the viewer's own
  // projects only. assigned_only scopes /projects/list to the caller's
  // user_projects rows for every role, super admin included.
  const fetchProjects = async () => {
    try {
      const params: Record<string, string | number | boolean> = {
        limit: 500,
        status: 'ACTIVE',
        assigned_only: true,
      };
      const response = await api.get('/projects/list', { params });
      if (response.data.success) setProjects(response.data.data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await api.get('/activities/list', {
        params: { limit: 100, status: 'ACTIVE' },
      });
      if (response.data.success)
        setActivities(response.data.data.activities || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchUsersByRole = async (roleId: number) => {
    try {
      const response = await api.get(`/survey/users/role/${roleId}`);
      if (response.data.success) {
        setUsers((prev) => ({
          ...prev,
          [roleId]: response.data.data.users || [],
        }));
      }
    } catch (error) {
      console.error('Error fetching users by role:', error);
    }
  };

  const updateAssignment = async (
    siteId: number,
    data: any,
    assignmentType: string,
  ) => {
    setActionLoading(true);
    try {
      const payload: any = { site_ids: [siteId] };
      switch (assignmentType) {
        case 'site_engineer':
          payload.site_engineer_userid = data.user.value;
          break;
        case 'ta':
          payload.ta_userid = data.user.value;
          break;
        case 'mis':
          payload.mis_userid = data.user.value;
          break;
        case 'report_maker':
          payload.report_designer_userid = data.user.value;
          break;
        case 'validator':
          payload.validator_userid = data.user.value;
          break;
      }
      const response = await api.put(
        '/survey/sites/update-assignment',
        payload,
      );
      if (response.data.success) {
        await fetchSites(
          searchTerm,
          statusFilter,
          projectFilter,
          activityFilter,
        );
      }
    } catch (error: any) {
      // A 409 here is the engineer backlog guard — its message names the sites
      // blocking the assignment, so it has to reach the assigner.
      console.error(`Error assigning ${assignmentType}:`, error);
      toast.error(
        error?.response?.data?.message || `Failed to assign ${assignmentType}`,
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
  };

  useEffect(() => {
    fetchSites(
      debouncedSearch,
      statusFilter,
      projectFilter,
      activityFilter,
      1,
      undefined,
      advancedFilters,
      sortConfig,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    fetchSites(searchTerm, status, projectFilter, activityFilter, 1, undefined, advancedFilters, sortConfig);
  };

  const handleProjectFilter = (project_id: string) => {
    setProjectFilter(project_id);
    fetchSites(searchTerm, statusFilter, project_id, activityFilter, 1, undefined, advancedFilters, sortConfig);
  };

  const handleActivityFilter = (activity_id: string) => {
    setActivityFilter(activity_id);
    fetchSites(searchTerm, statusFilter, projectFilter, activity_id, 1, undefined, advancedFilters, sortConfig);
  };

  const handleAdvancedFilter = (filters: Filters) => {
    setAdvancedFilters(filters);
    setExportFilters(filters);
    fetchSites(searchTerm, statusFilter, projectFilter, activityFilter, 1, undefined, filters, sortConfig);
  };

  const handlePageChange = (page: number) => {
    fetchSites(searchTerm, statusFilter, projectFilter, activityFilter, page, undefined, advancedFilters, sortConfig);
  };

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
    fetchSites(searchTerm, statusFilter, projectFilter, activityFilter, 1, limit, advancedFilters, sortConfig);
  };

  const handleSort = (field: string, direction: 'ASC' | 'DESC') => {
    const sort = { field, direction };
    setSortConfig(sort);
    fetchSites(searchTerm, statusFilter, projectFilter, activityFilter, pagination.page, undefined, advancedFilters, sort);
  };

  const handleAddSite = () => navigate('/sites/create');
  const handleEditSite = async () => {};

  const handleAssignEngineer = (site: Site, data: any) => {
    if (site.site_engineer) {
      updateAssignment(site.id, data, 'site_engineer');
    } else {
      api
        .post(`/survey/sites/${site.id}/assign-engineer`, data)
        .then(() => {
          fetchSites(searchTerm, statusFilter, projectFilter, activityFilter);
        })
        .catch((error: any) => {
          console.error('Error assigning engineer:', error);
          toast.error(
            error?.response?.data?.message || 'Failed to assign engineer',
          );
        });
    }
  };

  const handleAssignTA = (site: Site, data: any) =>
    updateAssignment(site.id, data, 'ta');
  const handleAssignMIS = (site: Site, data: any) =>
    updateAssignment(site.id, data, 'mis');

  const handleAssignReportMaker = (site: Site, data: any) => {
    if (site.report_designer) {
      updateAssignment(site.id, data, 'report_maker');
    } else {
      api
        .post(`/survey/sites/${site.id}/assign-report-maker`, data)
        .then(() => {
          fetchSites(searchTerm, statusFilter, projectFilter, activityFilter);
        });
    }
  };

  const handleAssignValidator = (site: Site, data: any) => {
    if (site.validator) {
      updateAssignment(site.id, data, 'validator');
    } else {
      api.post(`/survey/sites/${site.id}/assign-validator`, data).then(() => {
        fetchSites(searchTerm, statusFilter, projectFilter, activityFilter);
      });
    }
  };

  const handleCancelSite = async (site: Site, reason: string) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/survey/sites/${site.id}/cancel`, {
        cancel_reason: reason,
      });
      if (response.data.success)
        await fetchSites(searchTerm, statusFilter, projectFilter, activityFilter);
    } catch (error) {
      console.error('Error cancelling site:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleHoldSite = async (site: Site, reason: string) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/survey/sites/${site.id}/hold`, {
        hold_reason: reason,
      });
      if (response.data.success)
        await fetchSites(searchTerm, statusFilter, projectFilter, activityFilter);
    } catch (error) {
      console.error('Error putting site on hold:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResumeSite = async (site: Site) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/survey/sites/${site.id}/resume`);
      if (response.data.success)
        await fetchSites(searchTerm, statusFilter, projectFilter, activityFilter);
    } catch (error) {
      console.error('Error resuming site:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkAssign = async (
    siteIds: number[],
    data: any,
    type: 'reportMaker' | 'validator',
  ) => {
    setActionLoading(true);
    try {
      const payload: any = { site_ids: siteIds };
      if (type === 'reportMaker') {
        payload.report_designer_userid = data.user.value;
      } else {
        payload.validator_userid = data.user.value;
      }
      const response = await api.put('/survey/sites/update-assignment', payload);
      if (response.data.success) {
        await fetchSites(searchTerm, statusFilter, projectFilter, activityFilter);
      }
    } catch (error: any) {
      console.error(`Error bulk assigning ${type}:`, error);
      toast.error(
        error?.response?.data?.message || `Failed to bulk assign ${type}`,
      );
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Bulk "Send to Client" — the list has already checked the permission and
   * that every selected site is at Report Finalized; the server re-checks both
   * and names back anything it had to skip.
   */
  const handleBulkSendToClient = async (siteIds: number[], remarks?: string) => {
    setActionLoading(true);
    try {
      const response = await api.post('/survey/sites/bulk/send-to-client', {
        site_ids: siteIds,
        ...(remarks ? { remarks } : {}),
      });
      if (response.data.success) {
        const { updated_count = 0, skipped = [] } = response.data.data || {};
        if (updated_count > 0) {
          toast.success(
            `Report sent to client for ${updated_count} site${updated_count === 1 ? '' : 's'}`,
          );
        }
        if (Array.isArray(skipped) && skipped.length > 0) {
          toast.error(
            `${skipped.length} site${skipped.length === 1 ? ' was' : 's were'} skipped: ${skipped[0].reason}`,
          );
        }
        await fetchSites(searchTerm, statusFilter, projectFilter, activityFilter);
      }
    } catch (error: any) {
      console.error('Error sending reports to client:', error);
      toast.error(
        error?.response?.data?.message || 'Failed to send reports to client',
      );
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    setExportFilters(advancedFilters);
    fetchSites(
      searchTerm,
      statusFilter,
      projectFilter,
      activityFilter,
      pagination.page,
      undefined,
      advancedFilters,
      sortConfig,
    );
    fetchProjects();
    fetchActivities();
    fetchUsersByRole(2);
    fetchUsersByRole(5);
    fetchUsersByRole(6);
  }, [user?.id, user?.role?.level]);

  return (
    <div>
      <ProjectSitesList
        sites={sites}
        projects={projects}
        activities={activities}
        users={users}
        pagination={pagination}
        onSearch={handleSearch}
        onStatusFilter={handleStatusFilter}
        onProjectFilter={handleProjectFilter}
        onActivityFilter={handleActivityFilter}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onSort={handleSort}
        onAddSite={handleAddSite}
        onEditSite={handleEditSite}
        onAssignEngineer={handleAssignEngineer}
        onAssignTA={handleAssignTA}
        onAssignMIS={handleAssignMIS}
        onAssignReportMaker={handleAssignReportMaker}
        onAssignValidator={handleAssignValidator}
        onCancelSite={handleCancelSite}
        onHoldSite={handleHoldSite}
        onResumeSite={handleResumeSite}
        onAdvancedFilter={handleAdvancedFilter}
        onBulkAssign={handleBulkAssign}
        onBulkSendToClient={handleBulkSendToClient}
        loading={sitesLoading}
        actionLoading={actionLoading}
        exportFilters={exportFilters}
        exportSort={sortConfig}
        pendingWorkCounts={pendingWorkCounts}
        projectFilterOnly
      />
    </div>
  );
};

export default MySitesPage;
